import { OrganizationContext } from '../../../components/contexts/organization';
import { SearchFormValues } from '../list/List';
import {
  CouponPackAggregateJsonConnection,
  useInvokeCouponPackAggregateCsvUrlLazyQuery,
  useInvokeCouponPackAggregateJsonLazyQuery,
  usePollCouponPackAggregateCsvUrlLazyQuery,
  usePollCouponPackAggregateJsonLazyQuery,
} from '../../../graphql/generated';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Bugsnag from '@bugsnag/js';
import { downloadCsvFileWithBom } from '../../../util/download';
import { formatJstDate } from '../../../util';

const Limit = 25;

export type CouponPackUsageStatus = {
  organization?: OrganizationContext;
  loading: boolean;
  onSearch: (condition: SearchFormValues) => void;
  onCsvOut: (condition: SearchFormValues) => void;
  csvLoading: boolean;
  data?: CouponPackAggregateJsonConnection;
  onFetchMore: (condition: SearchFormValues) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  allAcquired: boolean;
};

export const useCouponPackUsageStatus = (): CouponPackUsageStatus => {
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext.organization;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const [offset, setOffset] = useState<number>(0);
  const [fetching, setFetching] = useState<boolean>(false);
  const [allAcquired, setAllAcquired] = useState<boolean>(false);

  // 利用状況集計 ポーリング
  const [pollJsonQuery, statusContext] = usePollCouponPackAggregateJsonLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first', // これをつけないと fetchMore 時に nextTokenなしで取得しにいってしまう
    pollInterval: 5000,
  });

  // 利用状況集計 取得
  // ApolloClient で ポーリングとfetchMoreを同時に利用できないため分ける
  const [getJsonQuery, getJsonQueryContext] = usePollCouponPackAggregateJsonLazyQuery({
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      if (Number(data?.pollCouponPackAggregateJson?.nodes?.length) < Limit) {
        setAllAcquired(true);
      }
      setLoading(false);
    },
  });

  // 利用状況集計取得 invoke
  const [invokeJsonQuery] = useInvokeCouponPackAggregateJsonLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      pollJsonQuery({
        variables: {
          queryExecutionId: data.invokeCouponPackAggregateJson.queryExecutionId,
        },
      });
      setOffset(offset + Limit);
    },
    onError: (e) => {
      setLoading(false);
      Bugsnag.notify(e);
      enqueueSnackbar('利用状況集計一覧の取得に失敗しました', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });

  // 利用状況一覧 CSV URL ポーリング
  const [pollCsvUrlQuery, statusCsvUrlContext] = usePollCouponPackAggregateCsvUrlLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    pollInterval: 5000,
  });

  // 利用状況CSV取得 invoke
  const [invokeCsvQuery] = useInvokeCouponPackAggregateCsvUrlLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      pollCsvUrlQuery({
        variables: {
          queryExecutionId: data.invokeCouponPackAggregateCsvUrl.queryExecutionId,
        },
      });
    },
    onError: (e) => {
      setCsvLoading(false);
      Bugsnag.notify(e);
      enqueueSnackbar('利用状況集計一覧の取得に失敗しました', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });

  // 検索実行
  const onSearchJsonSubmit = useCallback(
    (data: SearchFormValues) => {
      if (!organization) {
        return;
      }
      setOffset(0);
      setLoading(true);
      setAllAcquired(false);
      invokeJsonQuery({
        variables: {
          query: {
            adminOrganizationId: organization.id,
            couponPackCode: data.couponPackCode || undefined,
            couponPackName: data.couponPackName || undefined,
            startDate: data.startDate ? formatJstDate(new Date(data.startDate).toISOString()) : undefined,
            endDate: data.endDate ? formatJstDate(new Date(data.endDate).toISOString()) : undefined,
            limit: Limit,
            offset: offset,
          },
        },
      });
    },
    [organization, invokeJsonQuery]
  );

  // CSV出力
  const onCsvOutSubmit = useCallback(
    (data: SearchFormValues) => {
      if (!organization) {
        return;
      }
      setCsvLoading(true);
      invokeCsvQuery({
        variables: {
          query: {
            adminOrganizationId: organization.id,
            couponPackCode: data.couponPackCode || undefined,
            couponPackName: data.couponPackName || undefined,
            startDate: data.startDate ? formatJstDate(new Date(data.startDate).toISOString()) : undefined,
            endDate: data.endDate ? formatJstDate(new Date(data.endDate).toISOString()) : undefined,
          },
        },
      });
    },
    [organization, invokeCsvQuery]
  );

  // JSONクエリ出力をポーリングする
  // ポーリングクエリの onComplete は呼ばれないので useEffect でやるしかない
  useEffect(() => {
    const status = statusContext?.data?.pollCouponPackAggregateJson?.status;
    const hasError = statusContext?.error;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'CANCELLED' || status === 'FAILED' || status === 'SUCCEEDED' || hasError) {
      if (statusContext && statusContext.stopPolling) {
        statusContext.stopPolling();
      }
    }

    // 正常に終了した場合は、データフェッチ用のクエリを呼び出してステータスを終了にする
    if (
      status === 'SUCCEEDED' &&
      statusContext &&
      statusContext.data &&
      statusContext.data.pollCouponPackAggregateJson &&
      statusContext.data.pollCouponPackAggregateJson.nodes
    ) {
      if (
        fetching &&
        organization &&
        getJsonQueryContext &&
        getJsonQueryContext.fetchMore &&
        getJsonQueryContext.variables
      ) {
        setLoading(true);
        getJsonQueryContext.fetchMore({
          variables: statusContext.variables,
          updateQuery: (prevQuery, { fetchMoreResult }) => {
            setLoading(false);
            if (!fetchMoreResult) {
              return prevQuery;
            }
            // 取得件数が0件の場合は、全件取得済みとみなす
            if (Number(fetchMoreResult?.pollCouponPackAggregateJson?.nodes?.length) < Limit) {
              setAllAcquired(true);
              enqueueSnackbar('全件取得済みです', { variant: 'success' });
            } else {
              enqueueSnackbar('集計処理が正常に終了しました', { variant: 'success' });
            }
            return {
              pollCouponPackAggregateJson: {
                __typename: fetchMoreResult.pollCouponPackAggregateJson.__typename,
                queryExecutionId: fetchMoreResult.pollCouponPackAggregateJson.queryExecutionId,
                status: fetchMoreResult.pollCouponPackAggregateJson.status,
                nodes: [
                  ...(prevQuery.pollCouponPackAggregateJson.nodes || []),
                  ...(fetchMoreResult.pollCouponPackAggregateJson.nodes || []),
                ],
                pageInfo: fetchMoreResult.pollCouponPackAggregateJson.pageInfo,
              },
            };
          },
        });
        setFetching(false);
      } else {
        getJsonQuery({
          variables: statusContext.variables,
        });
        enqueueSnackbar('集計処理が正常に終了しました', { variant: 'success' });
      }
    } else if (status === 'FAILED' || status === 'CANCELLED' || hasError) {
      setLoading(false);
      enqueueSnackbar('集計処理でエラーが発生しました', {
        variant: 'error',
        autoHideDuration: 5000,
      });
      Bugsnag.notify(statusContext.error || new Error('polling error'));
    }
  }, [statusContext.data?.pollCouponPackAggregateJson.status, statusContext?.error]);

  // CSV URL クエリ出力をポーリングする
  // ポーリングクエリの onComplete は呼ばれないので useEffect でやるしかない
  useEffect(() => {
    const status = statusCsvUrlContext?.data?.pollCouponPackAggregateCsvUrl?.status;
    const hasError = statusCsvUrlContext?.error;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'CANCELLED' || status === 'FAILED' || status === 'SUCCEEDED' || hasError) {
      if (statusCsvUrlContext && statusCsvUrlContext.stopPolling) {
        statusCsvUrlContext.stopPolling();
      }
    }

    // 正常に終了した場合は、CSVファイルをダウンロード
    if (
      status === 'SUCCEEDED' &&
      statusCsvUrlContext &&
      statusCsvUrlContext.data &&
      statusCsvUrlContext.data.pollCouponPackAggregateCsvUrl
    ) {
      enqueueSnackbar('集計処理が正常に終了しました。CSVファイルをダウンロードします。', { variant: 'success' });
      setCsvLoading(false);
      if (statusCsvUrlContext.data.pollCouponPackAggregateCsvUrl.preSignedUrl)
        downloadCsvFileWithBom(statusCsvUrlContext.data.pollCouponPackAggregateCsvUrl.preSignedUrl);
    } else if (status === 'FAILED' || status === 'CANCELLED' || hasError) {
      setCsvLoading(false);
      enqueueSnackbar('集計処理でエラーが発生しました', {
        variant: 'error',
        autoHideDuration: 5000,
      });
      Bugsnag.notify(statusCsvUrlContext.error || new Error('polling error'));
    }
  }, [
    statusCsvUrlContext.data?.pollCouponPackAggregateCsvUrl.status,
    statusCsvUrlContext.data?.pollCouponPackAggregateCsvUrl.preSignedUrl,
    statusCsvUrlContext?.error,
  ]);

  // 更に読み込む
  const onFetchMoreJson = useCallback(
    async (data: SearchFormValues) => {
      if (statusContext && statusContext.stopPolling) {
        statusContext?.stopPolling();
      }
      if (organization && statusContext && statusContext.fetchMore && statusContext.variables) {
        setLoading(true);
        setFetching(true);
        await invokeJsonQuery({
          variables: {
            query: {
              adminOrganizationId: organization.id,
              couponPackCode: data.couponPackCode || undefined,
              couponPackName: data.couponPackName || undefined,
              startDate: data.startDate ? formatJstDate(new Date(data.startDate).toISOString()) : undefined,
              endDate: data.endDate ? formatJstDate(new Date(data.endDate).toISOString()) : undefined,
              limit: Limit,
              offset: offset,
            },
          },
        });
      }
    },
    [statusContext, getJsonQueryContext, organization]
  );

  return {
    organization,
    loading,
    onSearch: onSearchJsonSubmit,
    onCsvOut: onCsvOutSubmit,
    csvLoading,
    data: getJsonQueryContext.data?.pollCouponPackAggregateJson,
    onFetchMore: onFetchMoreJson,
    open,
    setOpen,
    allAcquired,
  };
};
